import type { ReactNode } from "react";
import type { PointState } from "../generated/widget-fixture";
import { Frame, Notes, useWidgetText } from "../shared";
import { compassSector, spokenValue } from "../station";
import type { WidgetProps } from "../types";

const SMALL = ["humidity", "wind", "rain_today", "pressure", "uv"] as const;
type Small = (typeof SMALL)[number];
const SPOKEN_KEY = { humidity: "station.spokenHumidity", pressure: "station.spokenPressure", uv: "station.spokenUv" };

/** The plan placeholder of the Pro widgets (PST-04, WSW-20): See plans for who may change the plan, else who can. */
export function ProLocked({ props, label }: { props: WidgetProps; label: (text: string) => string }) {
  const { widget, input, onSeePlans } = props;
  const fmt = useWidgetText();
  const text = fmt.t("widget.proLocked");
  return (
    <Frame widget={widget} state="locked" title={widget.title} label={label(text)}>
      <p className="font-medium text-gray-900">{text}</p>
      {input.can_change_plan ? (
        <div className="mt-auto">
          <button
            type="button"
            onClick={onSeePlans}
            className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-900 hover:bg-gray-50"
          >
            {fmt.t("statistics.seePlans")}
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-700">{fmt.t("statistics.askAdmin")}</p>
      )}
    </Frame>
  );
}

/**
 * weather_station (weather-station-widgets WSW-01 to WSW-06): one device's air temperature large, and under it
 * humidity, wind, rain today, pressure and UV, found by the slot the server gives each value. A value older than
 * twice the report interval shows its age; an offline device says so once.
 */
export function WeatherStation(props: WidgetProps) {
  const { widget, input, now } = props;
  const fmt = useWidgetText();
  const id = widget.source?.devices?.[0] ?? "";
  const device = input.devices?.[id];
  const deviceName = device?.name ?? "";
  const label = (values: string) => fmt.t("station.label", { device: deviceName, values });
  const bySlot: Record<string, PointState> = {};
  let rainKey: string | undefined;
  for (const [k, p] of Object.entries(input.points ?? {})) {
    if (!k.startsWith(`${id}/`) || !p.station_slot || bySlot[p.station_slot]) continue;
    bySlot[p.station_slot] = p;
    if (p.station_slot === "rain") rainKey = k;
  }
  const rain = rainKey ? input.statistics?.[rainKey] : undefined;
  if ((device?.features && !device.features.includes("statistics")) || rain?.problem?.code === "plan_feature") {
    return <ProLocked props={props} label={label} />;
  }
  const num = (p?: PointState) => (p && typeof p.latest?.value === "number" ? p.latest.value : undefined);
  const temp = bySlot.temperature;
  const tempValue = num(temp);
  const title = widget.title ?? deviceName;
  if (!temp || tempValue === undefined) {
    return (
      <Frame widget={widget} state="empty" title={title} label={label(fmt.t("state.noData"))}>
        <p className="text-gray-700">{fmt.t("state.noData")}</p>
      </Frame>
    );
  }
  const offline = device?.state === "offline";
  const every = device?.report_interval_s;
  const age = (p?: PointState) =>
    !offline && every && p?.latest && now.getTime() - Date.parse(p.latest.at) > 2 * every * 1000
      ? fmt.age(p.latest.at, now)
      : undefined;

  const configured = Array.isArray(widget.config?.slots) ? (widget.config.slots as string[]) : SMALL;
  const today = rain?.periods?.find((p) => p.period === "today");
  const present: Record<Small, boolean> = {
    humidity: num(bySlot.humidity) !== undefined,
    wind: num(bySlot.wind_speed) !== undefined,
    rain_today: !!bySlot.rain && !rain?.problem,
    pressure: num(bySlot.pressure) !== undefined,
    uv: num(bySlot.uv) !== undefined,
  };
  const slots = configured.filter((s): s is Small => (SMALL as readonly string[]).includes(s) && present[s as Small]);

  const spoken: string[] = [spokenValue(tempValue, temp, fmt.locale, fmt.t)];
  const cells = slots.map((slot) => {
    switch (slot) {
      case "wind": {
        const speed = bySlot.wind_speed;
        if (!speed) return null;
        const gust = num(bySlot.wind_gust);
        const deg = num(bySlot.wind_direction);
        const v = num(speed) ?? 0;
        const sector = deg === undefined ? undefined : compassSector(deg);
        const said = spokenValue(v, speed, fmt.locale, fmt.t);
        spoken.push(
          sector
            ? fmt.t("station.spokenWind", { direction: fmt.t(`compassName.${sector}`), value: said })
            : `${fmt.t("station.wind").toLocaleLowerCase(fmt.locale)} ${said}`,
        );
        const gustText = gust === undefined ? undefined : fmt.value(gust, { unit: null, decimals: speed.decimals });
        if (gustText) spoken.push(fmt.t("station.spokenGusts", { value: gustText }));
        return (
          <Cell key={slot} name={fmt.t("station.wind")} value={fmt.value(v, speed)} age={age(speed)}>
            {gustText && <span className="text-xs text-gray-700">{fmt.t("station.gusts", { value: gustText })}</span>}
            {sector && deg !== undefined && (
              <span className="inline-flex items-center gap-0.5 text-xs text-gray-700">
                <svg viewBox="0 0 20 20" className="size-3.5" aria-hidden="true">
                  {/* The arrow points where the wind blows to: from `deg`, so rotated by deg + 180 (WSW-04). */}
                  <path
                    d="M10 2 L14 15 L10 12 L6 15 Z"
                    className="fill-brand"
                    transform={`rotate(${deg + 180} 10 10)`}
                  />
                </svg>
                <span>{fmt.t(`compass.${sector}`)}</span>
              </span>
            )}
          </Cell>
        );
      }
      case "rain_today": {
        const p = bySlot.rain;
        if (!p) return null;
        const v = today?.value;
        if (typeof v === "number")
          spoken.push(fmt.t("station.spokenRainToday", { value: spokenValue(v, p, fmt.locale, fmt.t) }));
        return (
          <Cell key={slot} name={fmt.t("station.rainToday")} value={typeof v === "number" ? fmt.value(v, p) : "—"} />
        );
      }
      default: {
        const p = bySlot[slot];
        if (!p) return null;
        const v = num(p) ?? 0;
        spoken.push(fmt.t(SPOKEN_KEY[slot], { value: spokenValue(v, p, fmt.locale, fmt.t) }));
        return <Cell key={slot} name={fmt.t(`station.${slot}`)} value={fmt.value(v, p)} age={age(p)} />;
      }
    }
  });
  const seen = device?.last_seen_at;
  return (
    <Frame widget={widget} state={offline ? "offline" : "normal"} title={title} label={label(spoken.join(", "))}>
      <p className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
          {fmt.value(tempValue, temp)}
        </span>
        {age(temp) && <span className="text-xs text-gray-700">{age(temp)}</span>}
      </p>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 @xs:grid-cols-3">{cells}</dl>
      {offline && seen && <Notes items={[{ text: fmt.t("statistics.offline", { age: fmt.age(seen, now) }) }]} />}
    </Frame>
  );
}

function Cell({ name, value, age, children }: { name: string; value: string; age?: string; children?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-gray-700">{name}</dt>
      <dd className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="font-semibold text-gray-900 tabular-nums">{value}</span>
        {children}
        {age && <span className="text-xs text-gray-700">{age}</span>}
      </dd>
    </div>
  );
}
